// The Cobra-style EVM progress table (Cesar, Oct 20): the layout Deltek Cobra produced and the big EPCs kept, plus
// "Cesar Add-On" columns. Every row, subtotal and total computes its own figures from its own sums (never adds up
// row indices). Nothing new in the engine: it lays out metricsAt() results.
import { group, metricsAt, type Agg, type CodeFilter, type Metrics, type Prepared } from "../engine/compute";
import type { Breakdown, Settings, Unit } from "../engine/types";

export type EacBasis = "eac1" | "basic";

export interface CobraValues {
  /** Original budget: weights before any change order (budget in effect in the first period). */
  orig: number;
  bac: number;
  acPeriod: number;
  ac: number;
  pctExpended: number | null;
  evPeriod: number;
  ev: number;
  pctPeriod: number | null;
  pctComplete: number | null;
  pv: number;
  pctPlanned: number | null;
  pfPeriod: number | null;
  pf: number | null;
  /** Equivalent craft (headcount) this period: actual hours ÷ the period's standard hours. */
  craft: number | null;
  /** Planned craft this period: planned hours ÷ the period's standard hours. */
  craftPlan: number | null;
  spi: number | null;
  csi: number | null;
  eac1: number | null;
  eacBasic: number;
  vac: number | null;
  tcpi: number | null;
  /** TCPI to the revised forecast: (BAC − EV) ÷ (EAC − AC), with the chosen EAC. */
  tcpiEac: number | null;
  remainingEarn: number;
  remainingBurn: number;
}

export interface CobraRow {
  kind: "row" | "subtotal" | "total" | "indirect" | "grand";
  code?: string;
  label: string;
  v: CobraValues;
}

export interface CobraSection {
  label: string | null;
  rows: CobraRow[];
  subtotal: CobraRow | null;
}

export interface CobraTable {
  sections: CobraSection[];
  total: CobraRow;
  /** With the indirect layer: indirect rows, and the total including them. */
  indirect: { rows: CobraRow[]; grand: CobraRow } | null;
  stdHours: number;
}

const div = (a: number, b: number): number | null => (b === 0 || !isFinite(b) ? null : a / b);

export function cobraValues(m: Metrics, orig: number, stdHours: number, eac: EacBasis, mult = 1): CobraValues {
  const eacUsed = eac === "eac1" ? m.eac1 : m.eacBasic;
  return {
    orig: orig * mult,
    bac: m.bac * mult,
    acPeriod: m.acPeriod * mult,
    ac: m.ac * mult,
    pctExpended: m.pctExpended,
    evPeriod: m.evPeriod * mult,
    ev: m.ev * mult,
    pctPeriod: div(m.evPeriod, m.bac),
    pctComplete: m.pctEarned,
    pv: m.pv * mult,
    pctPlanned: m.pctPlanned,
    pfPeriod: m.cpiPeriod,
    pf: m.cpi,
    craft: div(m.acPeriod, stdHours),
    craftPlan: div(m.pvPeriod, stdHours),
    spi: m.spi,
    csi: m.csi,
    eac1: m.eac1 === null ? null : m.eac1 * mult,
    eacBasic: m.eacBasic * mult,
    vac: m.vac === null ? null : m.vac * mult,
    tcpi: m.tcpi,
    tcpiEac: eacUsed === null ? null : div(m.bac - m.ev, eacUsed - m.ac),
    remainingEarn: (m.bac - m.ev) * mult,
    remainingBurn: (m.bac - m.ac) * mult,
  };
}

/** Budget in effect in the first period = before any change order (approved COs take effect later, rule 17). */
const original = (a: Agg, s: Settings) => (a.bac[0] ?? 0) + (s.includeIndirects ? a.bacInd : 0);

/** The same group with only its indirect layer, measured with indirects switched on. */
function indirectOnly(a: Agg): Agg {
  const z = (x: Float64Array) => new Float64Array(x.length);
  return { ...a, bac: z(a.bac), pv: z(a.pv), ev: z(a.ev), fv: z(a.fv), ac: z(a.ac) };
}

export interface CobraOptions {
  /** Breakdown for the rows (normally DBS). */
  rowsBy: Breakdown;
  /** Sections: none, the DBS type (discipline / procurement), or another breakdown (e.g. PBS phase). */
  sectionsBy: "none" | "dbsType" | Breakdown;
  filter: CodeFilter;
  asOf: number;
  unit: Unit;
  settings: Settings;
  eac: EacBasis;
  /** Hours → $ when the cost view is on. */
  mult: number;
  indirectLayer: boolean;
  titles: Partial<Record<Breakdown, Map<string, string>>>;
  dbsTypes: Map<string, "discipline" | "procurement" | undefined>;
}

export function buildCobra(prep: Prepared, o: CobraOptions): CobraTable {
  const s = o.settings;
  const std = prep.data.periods[o.asOf]?.stdHours ?? 0;
  const vals = (a: Agg, settings: Settings = s) => cobraValues(metricsAt(a, o.asOf, settings), original(a, settings), std, o.eac, o.mult);
  const nonEmpty = (a: Agg) => {
    const m = metricsAt(a, o.asOf, s);
    return !!(m.bac || m.pv || m.ev || m.ac || m.fv);
  };
  const name = (bd: Breakdown, code: string) => {
    const t = o.titles[bd]?.get(code);
    return code ? (t ? `${code} - ${t}` : code) : "(none)";
  };
  const rowsFor = (filter: CodeFilter): CobraRow[] =>
    group(prep, { by: [o.rowsBy], unit: o.unit, filter })
      .groups.filter(nonEmpty)
      .map((g) => ({ kind: "row" as const, code: g.key[0], label: name(o.rowsBy, g.key[0]), v: vals(g) }));
  const subtotalFor = (label: string, filter: CodeFilter): CobraRow => ({ kind: "subtotal", label, v: vals(group(prep, { by: [], unit: o.unit, filter }).total) });

  const sections: CobraSection[] = [];
  if (o.sectionsBy === "none") sections.push({ label: null, rows: rowsFor(o.filter), subtotal: null });
  else if (o.sectionsBy === "dbsType") {
    // Discipline (direct) work first, then procurement; a DBS without a type counts as discipline.
    const codes = group(prep, { by: ["dbs"], unit: o.unit, filter: o.filter }).groups.map((g) => g.key[0]);
    for (const [type, label] of [
      ["discipline", "Direct engineering"],
      ["procurement", "Procurement"],
    ] as const) {
      const mine = codes.filter((c) => (o.dbsTypes.get(c) ?? "discipline") === type);
      if (!mine.length) continue;
      const filter = { ...o.filter, dbs: o.filter.dbs?.length ? o.filter.dbs.filter((c) => mine.includes(c)) : mine };
      const rows = rowsFor(filter);
      if (rows.length) sections.push({ label, rows, subtotal: subtotalFor(`Total ${label.toLowerCase()}`, filter) });
    }
  } else {
    const bd = o.sectionsBy;
    for (const g of group(prep, { by: [bd], unit: o.unit, filter: o.filter }).groups.filter(nonEmpty)) {
      const filter = { ...o.filter, [bd]: [g.key[0]] };
      const label = name(bd, g.key[0]);
      sections.push({ label, rows: rowsFor(filter), subtotal: subtotalFor(`Total ${label}`, filter) });
    }
  }
  // A single section's subtotal would repeat the total.
  if (sections.length === 1) sections[0].subtotal = null;
  const all = group(prep, { by: [o.rowsBy], unit: o.unit, filter: o.filter });
  const total: CobraRow = { kind: "total", label: "Total", v: vals(all.total) };

  let indirect: CobraTable["indirect"] = null;
  if (o.indirectLayer && !s.includeIndirects) {
    const withInd = { ...s, includeIndirects: true };
    const rows = all.groups
      .filter((g) => g.bacInd || g.acInd.some((x) => x) || g.pvInd.some((x) => x))
      .map((g) => ({ kind: "indirect" as const, code: g.key[0], label: `${name(o.rowsBy, g.key[0])} (indirect)`, v: vals(indirectOnly(g), withInd) }));
    if (rows.length) indirect = { rows, grand: { kind: "grand", label: "Total including indirects", v: vals(all.total, withInd) } };
  }
  return { sections, total, indirect, stdHours: std };
}

/** Column layout shared by the screen table and the Excel export. */
export interface CobraColumn {
  key: keyof CobraValues;
  group: string;
  /** Header lines under the group (e.g. "This", "Period"). */
  head: [string, string];
  /** Formula label above the header (Cesar's "BAC", "AC[cum]"...). */
  tag: string;
  fmt: "num" | "pct" | "idx" | "hc";
  /** Yellow = data from progress and hours; grey = calculated; addon = Cesar Add-On. */
  tone: "plain" | "data" | "calc" | "addon";
}

export const COBRA_COLUMNS: CobraColumn[] = [
  { key: "orig", group: "Baseline", head: ["Budget", "Hours"], tag: "Original", fmt: "num", tone: "plain" },
  { key: "bac", group: "Current", head: ["Approved", "Hours"], tag: "BAC", fmt: "num", tone: "plain" },
  { key: "acPeriod", group: "Expended", head: ["This", "Period"], tag: "AC[periodic]", fmt: "num", tone: "data" },
  { key: "ac", group: "Expended", head: ["Project", "To Date"], tag: "AC[cum]", fmt: "num", tone: "data" },
  { key: "pctExpended", group: "Percent Expended", head: ["Of Current", "Hours"], tag: "AC ÷ BAC", fmt: "pct", tone: "calc" },
  { key: "evPeriod", group: "Earned", head: ["This", "Period"], tag: "EV[periodic]", fmt: "num", tone: "data" },
  { key: "ev", group: "Earned", head: ["Project", "To Date"], tag: "EV[cum]", fmt: "num", tone: "plain" },
  { key: "pctPeriod", group: "% Complete", head: ["This", "Period"], tag: "EV ÷ BAC", fmt: "pct", tone: "calc" },
  { key: "pctComplete", group: "% Complete", head: ["Project", "To Date"], tag: "EV ÷ BAC", fmt: "pct", tone: "calc" },
  { key: "pv", group: "Planned", head: ["Hours", "To Date"], tag: "PV[cum]", fmt: "num", tone: "plain" },
  { key: "pctPlanned", group: "Planned", head: ["Progress", "To Date"], tag: "PV ÷ BAC", fmt: "pct", tone: "calc" },
  { key: "pfPeriod", group: "Performance Factor", head: ["This", "Period"], tag: "CPI[periodic]", fmt: "idx", tone: "calc" },
  { key: "pf", group: "Performance Factor", head: ["Project", "To Date"], tag: "CPI[cum]", fmt: "idx", tone: "calc" },
  { key: "craft", group: "Craft", head: ["Equiv. Craft", "This Period"], tag: "AC ÷ period h", fmt: "hc", tone: "calc" },
  { key: "craftPlan", group: "Craft", head: ["Plan Craft", "This Period"], tag: "PV ÷ period h", fmt: "hc", tone: "calc" },
  { key: "spi", group: "SPI", head: ["Schedule Perf.", "Index Cum"], tag: "EV ÷ PV", fmt: "idx", tone: "addon" },
  { key: "csi", group: "CSI", head: ["Cost Schedule", "Index Cum"], tag: "CPI × SPI", fmt: "idx", tone: "addon" },
  { key: "eac1", group: "EAC1", head: ["Estimate At", "Completion"], tag: "(BAC−EV)÷CPI+AC", fmt: "num", tone: "addon" },
  { key: "eacBasic", group: "EAC Basic", head: ["Estimate At", "Completion"], tag: "(BAC−EV)+AC", fmt: "num", tone: "addon" },
  { key: "vac", group: "VAC", head: ["Variance At", "Completion"], tag: "BAC − EAC1", fmt: "num", tone: "addon" },
  { key: "tcpi", group: "TCPI1", head: ["To Complete", "Index Cum"], tag: "(BAC−EV)÷(BAC−AC)", fmt: "idx", tone: "addon" },
  { key: "tcpiEac", group: "TCPI EAC", head: ["Perf. to EAC", "Index Cum"], tag: "(BAC−EV)÷(EAC−AC)", fmt: "idx", tone: "addon" },
  { key: "remainingEarn", group: "Remaining", head: ["to Earn", "BAC − EV"], tag: "BAC − EV", fmt: "num", tone: "addon" },
  { key: "remainingBurn", group: "Remaining", head: ["to \"Burn\"", "BAC − AC"], tag: "BAC − AC", fmt: "num", tone: "addon" },
];

export const FORMULAS: [string, string][] = [
  ["Schedule Variance", "SV = EV − PV"],
  ["Schedule Performance Index", "SPI = EV ÷ PV"],
  ["Cost Variance", "CV = EV − AC"],
  ["Cost Performance Index (PF)", "CPI = EV ÷ AC"],
  ["Cost Schedule Index", "CSI = CPI[cum] × SPI[cum]"],
  ["Estimate to Complete", "ETC = (BAC − EV[cum]) ÷ CPI[cum]"],
  ["Estimate At Completion (EAC) 1", "EAC1 = (BAC − EV[cum]) ÷ CPI[cum] + AC[cum]"],
  ["Estimate At Completion – Basic", "EAC Basic = (BAC − EV[cum]) + AC[cum]"],
  ["Variance At Completion", "VAC = BAC − EAC1 (negative = overrun)"],
  ["To Complete Performance Index (to BAC)", "TCPI1 = (BAC − EV) ÷ (BAC − AC[cum])"],
  ["To Complete Performance Index (to EAC)", "TCPI EAC = (BAC − EV) ÷ (EAC − AC[cum]). Use when management accepts the revised EAC as the target."],
  ["Baseline Budget Hours", "Budget before change orders"],
  ["Current Approved Hours (BAC)", "Baseline + approved change orders"],
];
